import { useState } from "react";
import { Fingerprint, RssSimple, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

const compact = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact" }).format(n));

// Client-side detection of what the user pasted — helpful hint before hitting the API
const detectInputType = (raw) => {
  const q = raw.trim();
  if (!q) return "";
  if (/^UC[A-Za-z0-9_-]{22}$/.test(q)) return "Channel ID";
  if (/\/channel\/UC[A-Za-z0-9_-]{22}/.test(q)) return "Channel URL";
  if (/@[A-Za-z0-9_.\-]+/.test(q)) return "Handle";
  if (/(youtube\.com\/watch\?v=|youtu\.be\/|\/shorts\/)/.test(q)) return "Video URL";
  if (/youtube\.com\/(c|user)\//.test(q)) return "Legacy custom URL";
  return "Channel name (search)";
};

const InfoRow = ({ label, value, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-[var(--border-c)] last:border-0">
    <span className="text-xs uppercase tracking-widest text-[var(--muted)] sm:w-40 shrink-0">{label}</span>
    <code data-testid={testId} className="text-sm text-[var(--text)] break-all flex-1 font-mono">{value}</code>
    <CopyButton text={value} testId={`${testId}-copy`} className="shrink-0" />
  </div>
);

export default function ChannelIdConverter() {
  useSeo(
    "YouTube Channel ID Converter",
    "Paste any @handle, video URL, custom URL or channel link — instantly get the channel ID, handle, RSS feed and every URL variant."
  );
  const [query, setQuery] = useState("");
  const [ch, setCh] = useState(null);
  const [loading, setLoading] = useState(false);
  const detected = detectInputType(query);

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setCh(null);
    try {
      const res = await api.get("/channel", { params: { query } });
      setCh(res.data);
      api.post("/track", { tool: "channel_id_converter" }).catch(() => {});
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const id = ch?.channelId;
  const handle = ch?.handle && (ch.handle.startsWith("@") ? ch.handle : `@${ch.handle}`);
  const rows = ch
    ? [
        { label: "Channel ID", value: id, testId: "out-channel-id" },
        { label: "Handle", value: handle || "—", testId: "out-handle" },
        { label: "Canonical URL", value: `https://www.youtube.com/channel/${id}`, testId: "out-canonical-url" },
        ...(handle ? [{ label: "Handle URL", value: `https://www.youtube.com/${handle}`, testId: "out-handle-url" }] : []),
        { label: "RSS feed", value: `https://www.youtube.com/feeds/videos.xml?channel_id=${id}`, testId: "out-rss" },
        { label: "Embed all uploads", value: `https://www.youtube.com/embed?listType=user_uploads&list=${id}`, testId: "out-uploads-embed" },
      ]
    : [];

  return (
    <ToolShell
      slug="channel-id-converter"
      title="Channel ID Converter"
      tagline="Paste anything — @handle, video URL, /c/ link, raw ID — get every YouTube identifier for that channel."
      hasResults={!!ch}
    >
      <form onSubmit={search} className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <label className="text-sm font-semibold block mb-2">Any YouTube URL, handle or ID</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="converter-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="@mkbhd  •  youtube.com/watch?v=…  •  UCBJycsmduvYEL83R_U4JriQ"
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button
            data-testid="converter-submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <Fingerprint size={16} weight="bold" /> {loading ? "Resolving…" : "Convert"}
          </button>
        </div>
        {detected && (
          <p data-testid="converter-detected" className="text-xs text-[var(--muted)] mt-3">
            Detected: <span className="text-[var(--glow)] font-semibold">{detected}</span>
          </p>
        )}
      </form>

      {ch && (
        <div className="mt-8 surface rounded-[20px] p-6 sm:p-8 fade-up" data-testid="converter-result">
          <div className="flex items-center gap-4 pb-5 border-b border-[var(--border-c)]">
            {ch.avatar && <img src={ch.avatar} alt={ch.title} className="w-14 h-14 rounded-full" />}
            <div className="min-w-0 flex-1">
              <h2 className="font-bold text-lg truncate">{ch.title}</h2>
              <p className="text-[var(--muted)] text-xs">{compact(ch.subscriberCount)} subs · {compact(ch.videoCount)} videos</p>
            </div>
          </div>
          <div className="mt-3">
            {rows.map((r) => (
              <InfoRow key={r.label} label={r.label} value={r.value} testId={r.testId} />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            <a
              href={`https://www.youtube.com/channel/${id}`}
              target="_blank" rel="noreferrer"
              data-testid="open-channel-link"
              className="surface rounded-[12px] p-3 flex items-center gap-2 text-sm hover:border-[var(--glow)] transition-colors"
            >
              <LinkSimple size={16} weight="bold" /> Open channel
            </a>
            <a
              href={`https://www.youtube.com/feeds/videos.xml?channel_id=${id}`}
              target="_blank" rel="noreferrer"
              data-testid="open-rss-link"
              className="surface rounded-[12px] p-3 flex items-center gap-2 text-sm hover:border-[var(--glow)] transition-colors"
            >
              <RssSimple size={16} weight="bold" /> Open RSS feed
            </a>
          </div>
        </div>
      )}
    </ToolShell>
  );
}
